import { format } from 'date-fns';

export interface MonthBucket {
  start: Date;
  end: Date;
  label: string;
  count: number;
  totalValue: number;
  processingTimes: number[];
}

/**
 * Build 6 UTC-based calendar month buckets: current month + 5 previous months.
 * Matches SQL: date_trunc('month', created_at AT TIME ZONE 'UTC')
 */
export function buildUtcMonthBuckets(monthCount = 6): MonthBucket[] {
  const now = new Date();
  const utcYear = now.getUTCFullYear();
  const utcMonth = now.getUTCMonth();

  return Array.from({ length: monthCount }, (_, i) => {
    const offset = monthCount - 1 - i;
    const start = new Date(Date.UTC(utcYear, utcMonth - offset, 1));
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
    const label = format(start, 'MMM yyyy');
    return { start, end, label, count: 0, totalValue: 0, processingTimes: [] };
  });
}

/**
 * The ISO start string for the earliest bucket (for use in Supabase .gte() filters).
 */
export function getSixMonthStartISO(): string {
  const buckets = buildUtcMonthBuckets(6);
  return buckets[0].start.toISOString();
}

/**
 * Assign a created_at timestamp to its UTC month bucket.
 */
export function assignToBucket(buckets: MonthBucket[], createdAt: string, totalAmount: number) {
  const d = new Date(createdAt);
  for (const bucket of buckets) {
    if (d >= bucket.start && d < bucket.end) {
      bucket.count += 1;
      bucket.totalValue += totalAmount;
      return;
    }
  }
}

/**
 * Paginated fetch of procurements from Supabase to avoid the 1000-row limit.
 * Returns all rows matching the date filter with only the selected columns.
 */
export async function fetchAllProcurementsInRange(
  supabase: any,
  startISO: string,
  endISO?: string,
  columns = 'status, total_amount, total_quantity, created_at, delivery_date, product_name, rejection_source'
): Promise<any[]> {
  const PAGE_SIZE = 1000;
  let offset = 0;
  const allRows: any[] = [];

  while (true) {
    let query = supabase
      .from('procurements')
      .select(columns)
      .gte('created_at', startISO)
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (endISO) {
      query = query.lte('created_at', endISO);
    }

    const { data, error } = await query;
    if (error) throw error;
    if (!data || data.length === 0) break;

    allRows.push(...data);
    if (data.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return allRows;
}
